import React from 'react';
import { AlertTriangle } from 'lucide-react';
import styles from './ActivityFeed.module.css';

// The feed can accept either pre-normalized ActivityItem objects or raw
// ActivityEvent objects returned by the dashboard API. We'll handle both
// shapes dynamically so regulation events retain their original fields.
interface ActivityFeedProps {
  isLoading?: boolean;
  activities?: any[];
}

const getStatusIcon = (status: string): React.ReactNode => {
  switch (status) {
    case 'success':
      return '✓';
    case 'warning':
      return <AlertTriangle size={16} aria-hidden />;
    case 'info':
      return 'ℹ';
    case 'pending':
      return '○';
    default:
      return '●';
  }
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  isLoading = false,
  activities = []
}) => {
  const [tab, setTab] = React.useState<'users' | 'regulations'>('users');
  const [currentPage, setCurrentPage] = React.useState(1);
  const perPage = 3; // show 3 items per regulations page as requested

  // reset page when switching tabs
  React.useEffect(() => {
    setCurrentPage(1);
  }, [tab]);

  const tabEvents = (activities || []).filter((act) => {
    if (tab === 'users') return act.event_type === 'admin' || act.event_type === 'user';
    return act.event_type === 'regulation';
  });

  const totalPages = Math.max(1, Math.ceil(tabEvents.length / perPage));
  const visibleEvents = tab === 'regulations'
    ? tabEvents.slice((currentPage - 1) * perPage, currentPage * perPage)
    : tabEvents;
  if (isLoading) {
    return (
      <div className={`${styles.dataSection} ${styles.skeleton}`} />
    );
  }

  return (
    <div className={styles.dataSection}>
      <h3 className={styles.sectionTitle}>Real-time Activity Feed</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setTab('users')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: tab === 'users' ? '1px solid var(--primary-500)' : '1px solid transparent',
              background: tab === 'users' ? 'var(--ds-info-bg)' : 'transparent',
              cursor: 'pointer'
            }}
          >Users</button>
          <button
            onClick={() => setTab('regulations')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: tab === 'regulations' ? '1px solid var(--primary-500)' : '1px solid transparent',
              background: tab === 'regulations' ? 'var(--ds-info-bg)' : 'transparent',
              cursor: 'pointer'
            }}
          >Regulations</button>
        </div>
        <div style={{ color: 'var(--ds-text-muted)', fontSize: 12 }}>{activities.length} events</div>
      </div>
      <div className={styles.feedContainer}>
        <div className={styles.feedTable}>
          <div className={styles.feedHeader}>
            <div className={styles.feedHeaderCell}>Time</div>
            <div className={styles.feedHeaderCell}>Event</div>
            <div className={styles.feedHeaderCell}>{tab === 'regulations' ? 'Jurisdiction' : 'User'}</div>
            <div className={styles.feedHeaderCell}>Status</div>
          </div>
          {tabEvents.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--ds-text-faint)', fontSize: '0.9rem' }}>
              {tab === 'users' ? 'No recent admin/user activity.' : 'No recent regulation activity.'}
            </div>
          ) : (
            visibleEvents.map((activity: any) => {
              // Support both normalized items and raw dashboard events
              const isRaw = !!activity.timestamp;
              const id = activity.id || `${activity.event}-${Math.random().toString(36).slice(2,8)}`;
              // Backend sends naive UTC strings (no Z/+00:00); append Z so JS parses as UTC then converts to local time
              const rawTs = activity.timestamp || activity.time;
              const tsStr = typeof rawTs === 'string' && !rawTs.endsWith('Z') && !rawTs.includes('+') ? rawTs + 'Z' : rawTs;
              const ts = isRaw ? new Date(tsStr) : new Date(rawTs || Date.now());
              const dateLabel = ts.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
              const timeOnly = ts.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
              // For regulation events prefer the description/title (policy name)
              const eventLabel = activity.event_type === 'regulation'
                ? (activity.description || activity.title || 'Policy update')
                : (activity.event || activity.title || 'Event');
              const jurisdictionRaw = activity.jurisdiction || activity.jurisdiction_code || activity.jurisdiction_name || '';
              let jurisdictionLabel = jurisdictionRaw || '—';
              try {
                // If we have a 2-letter region code, show the full country name
                if (jurisdictionRaw && jurisdictionRaw.length === 2) {
                  // Intl.DisplayNames may not be available in some environments; guard it
                  // and fall back to the raw code if not supported.
                  // @ts-ignore
                  if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
                    // @ts-ignore
                    const dn = new (Intl as any).DisplayNames(['en'], { type: 'region' });
                    jurisdictionLabel = dn.of(jurisdictionRaw.toUpperCase()) || jurisdictionRaw.toUpperCase();
                  } else {
                    jurisdictionLabel = jurisdictionRaw.toUpperCase();
                  }
                }
              } catch (err) {
                jurisdictionLabel = jurisdictionRaw || '—';
              }
              const emailLabel = activity.user || '';
              const statusKey = activity.status || (activity.event_type === 'regulation' ? 'success' : 'info');

              return (
                <div key={id} className={`${styles.feedRow} ${styles[`feedRow_${statusKey}`]}`}>
                  <div className={`${styles.feedCell} ${styles.feedCellTime}`}>
                    <span className={styles.dateText}>{dateLabel}</span>
                    {activity.event_type !== 'regulation' && (
                      <span className={styles.timeSub}>{timeOnly}</span>
                    )}
                  </div>
                  <div className={`${styles.feedCell} ${styles.feedCellEvent}`}>
                    <div className={styles.eventTitle}>{eventLabel}</div>
                  </div>
                  <div className={`${styles.feedCell} ${styles.feedCellUser}`}>
                    {tab === 'regulations'
                      ? <span className={styles.jurisdictionText}>{jurisdictionLabel}</span>
                      : <span className={styles.userText}>{emailLabel || ''}</span>
                    }
                  </div>
                  <div className={styles.feedCell}>
                    <span className={`${styles.statusBadge} ${styles[`status_${statusKey}`]}`}>
                      {getStatusIcon(statusKey)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          {tab === 'regulations' && totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12 }}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={styles.paginationButton}
              >← Prev</button>
              <div style={{ alignSelf: 'center', color: 'var(--admin-text-muted, var(--ds-text-muted))' }}>Page {currentPage} of {totalPages}</div>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
              >Next →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default ActivityFeed;
